import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { getEnglishState, type EnglishState } from '../../../lib/englishApi.js';
import { subscribeEnglishState } from '../../leveling/useEnglishLevel.js';
import { listenLevelOf } from '../bookLib.js';
import { getCatalog } from '../listenApi.js';
import type { CatalogBook, Cefr } from '../types.js';

export interface UseListenBootstrapReturn {
  /** Nivel CEFR oficial del user (`cefrGlobal`), default del selector. */
  userLevel: Cefr | null;
  /** Catálogo completo (orden de libros) para navegar prev/next cruzando libros. */
  catalog: CatalogBook[];
  /** Ref siempre-actual del catálogo (para callbacks estables como restoreSession). */
  catalogRef: RefObject<CatalogBook[]>;
}

/**
 * Bootstrap del feature Escuchar: el nivel oficial del user y el catálogo.
 *
 * El catálogo se trae una vez al montar. El nivel, al montar Y en cada lectura
 * fresca de `/state` que haga la app (`subscribeEnglishState`): este provider
 * vive por encima de las rutas y monta una sola vez, muchas veces en /login,
 * donde su pedido da 401 — un alumno que se registra en la web llegaba a la
 * Audioteca sin nivel y quedaba en 'B1' (#575, 20/10). Ahora se entera cuando
 * entra, cuando termina el onboarding y cuando cambia de nivel, sin pedir
 * `/state` de más. Sin catálogo, prev/next quedan deshabilitados.
 */
export function useListenBootstrap(args: {
  /** Se llama con el nivel oficial cada vez que se conoce uno distinto del
   *  último (para sembrar el selector si el user nunca eligió un nivel).
   *  Capturado al montar — debe ser estable. */
  onOfficialLevel: (cefr: Cefr) => void;
}): UseListenBootstrapReturn {
  const { onOfficialLevel } = args;

  const [userLevel, setUserLevel] = useState<Cefr | null>(null);
  const [catalog, setCatalog] = useState<CatalogBook[]>([]);
  const catalogRef = useRef<CatalogBook[]>(catalog);
  catalogRef.current = catalog;

  useEffect(() => {
    let alive = true;
    /** El último nivel sembrado: otra lectura con el mismo no pisa lo que pasó en la visita. */
    let seeded: Cefr | null = null;
    const apply = (s: EnglishState): void => {
      if (!alive) return;
      const contentLevel = listenLevelOf(s.cefrGlobal);
      setUserLevel(contentLevel);
      // Sin nivel diagnosticado (alumno recién registrado, o cambio a una cuenta
      // sin nivel) no hay nada que sembrar: el selector queda donde está.
      // Sembrarlo con un nivel inventado sería peor que no sembrarlo (el user
      // vería "tu nivel" en un libro que nadie eligió).
      if (contentLevel === null) {
        seeded = null;
        return;
      }
      if (contentLevel === seeded) return;
      seeded = contentLevel;
      onOfficialLevel(contentLevel);
    };
    const off = subscribeEnglishState(apply);
    getEnglishState()
      .then(apply)
      .catch(() => {
        /* sin sesión / módulo: llega con la próxima lectura de `/state` */
      });
    return () => {
      alive = false;
      off();
    };
    // Deps vacías a propósito: se suscribe al montar (onOfficialLevel capturado).
  }, []);

  useEffect(() => {
    let alive = true;
    getCatalog()
      .then(({ books }) => {
        if (alive) setCatalog(books);
      })
      .catch(() => {
        /* sin catálogo → prev/next quedan deshabilitados (hasNext/hasPrev false) */
      });
    return () => {
      alive = false;
    };
  }, []);

  return { userLevel, catalog, catalogRef };
}
